import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'
import { QUANTUM_COLORS } from './constants.js'

const STATE_META = {
  price_above_upper: { label: 'UPPER', color: QUANTUM_COLORS.bear },
  price_below_lower: { label: 'LOWER', color: QUANTUM_COLORS.bull },
  price_inside: { label: 'INSIDE', color: QUANTUM_COLORS.neutral },
  squeeze: { label: 'SQUEEZE', color: QUANTUM_COLORS.squeeze },
}

const EVENT_LABELS = {
  touch_upper: 'Toque upper',
  touch_lower: 'Toque lower',
  squeeze_start: 'Inicio squeeze',
  reentry_inside: 'Vuelta dentro',
}

function fmtPrice(v) {
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 1000) return v.toFixed(1)
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

function fmtTime(t) {
  const n = typeof t === 'string' ? new Date(t).getTime() / 1000 : t
  if (!Number.isFinite(n)) return ''
  const d = new Date(n * 1000)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm} ${hh}:${mi}`
}

function fmtPct(v) {
  return Number.isFinite(v) ? `${v.toFixed(2)}%` : '—'
}

export default function BollingerBandsQuantumBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0',
  onHeaderMouseDown,
}) {
  const data = result || {}
  const stateMeta = STATE_META[data.state] || null
  const stateColor = stateMeta?.color ?? QUANTUM_COLORS.neutral
  const lastEvent = data.lastEvent || null

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          Bollinger Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && stateMeta && (
            <span className="ml-1.5 text-[10px]" style={{ color: stateColor }}>· {stateMeta.label}</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        )}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Sesgo</span>
              <span className="text-[11px] font-semibold" style={{ color: stateColor }}>
                {stateMeta ? stateMeta.label : '—'}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-0.5 px-2.5 pb-1">
            <Row label="Toques upper" value={data.upperTouches ?? 0} color={QUANTUM_COLORS.bear} />
            <Row label="Toques lower" value={data.lowerTouches ?? 0} color={QUANTUM_COLORS.bull} />
          </div>

          <div className="flex flex-col gap-0.5 px-2.5 pb-1">
            <Row label="Bandwidth" value={fmtPct(data.bandwidth)} color="var(--ink-soft, #4A5A52)" />
            <Row label="%B" value={fmtPct(data.percentB)} color="var(--ink-soft, #4A5A52)" />
          </div>

          <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
            <div className="pt-1 flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Último evento</span>
              {lastEvent ? (
                <span className="text-[10px] font-medium tabular-nums" style={{ color: stateColor }}>
                  {EVENT_LABELS[lastEvent.type] ?? lastEvent.type} · {fmtPrice(lastEvent.price)} · {fmtTime(lastEvent.time)}
                </span>
              ) : (
                <span className="text-[10px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>—</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Row({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>{label}</span>
      <span className="text-[10px] font-medium tabular-nums" style={{ color }}>{value}</span>
    </div>
  )
}
